import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api'
import { PageNumbers } from '../components/PageNumbers'
import { useAsync } from '../lib/useAsync'
import { ErrorState, LoadingBlock } from '../components/States'
import { plural } from '../lib/format'
import { storedAcross, useLookAgain } from '../lib/survey-status'
import { PAGE, latestOnly, lastWins, nextPageOpen, readPage } from '../lib/market-pages'
import type { Ask, Filled } from '../lib/market-pages'
import {
  surveyLabels, sideOf, foldFields, keyOf, FIELD_LABEL, FIELD_KIND,
} from '../lib/unify'
import type {
  ColumnValues,
  FilterClause,
  MarketPage,
  MarketRecord,
  MarketSurvey,
  SurveyColumn,
  ShownField,
} from '../types'

/**
 * The market data table.
 *
 * **Its columns are not known until the data arrives.** One survey has eleven
 * and the next has twenty two, and they are not the same eleven. Every column
 * on this screen comes from the chosen survey's own column list, in the
 * provider's own order, under the provider's own headings. Nothing here names
 * a column, and nothing may start to.
 */
export function MarketData() {
  const [q, setQ] = useState('')
  const [survey, setSurvey] = useState('')
  /**
   * What each narrowed column requires, keyed by its heading.
   *
   * Keyed by heading rather than by column key because on the every-survey
   * table one column is one heading drawn from several surveys, and the same
   * heading has a different key in each of them. The clause carries all of
   * those keys, so a row passes when any one of them matches.
   */
  const [narrow, setNarrow] = useState<Record<string, FilterClause>>({})
  const [page, setPage] = useState<MarketPage | null>(null)
  const [rows, setRows] = useState<MarketRecord[]>([])
  const [searching, setSearching] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  /** The page on screen stopped short after its most reads, with more to read. */
  const [paused, setPaused] = useState(false)

  /**
   * Which page is on screen, and where each page starts.
   *
   * `trail[n]` is the cursor that begins page n, and `trail[0]` is the start
   * of the table. The table can only be read in order, so a page nobody has
   * been to yet has no cursor and has to be walked to. Keeping the cursors
   * means going back to page 2 is one request rather than a walk from the top.
   */
  const [at, setAt] = useState(0)
  const [trail, setTrail] = useState<(string | null)[]>([null])

  const surveys = useAsync(() => api.listSurveys(), [])
  // While a survey is being stored or prepared, its figure moves (FR-M62).
  useLookAgain(surveys, api.listSurveys)
  /*
   * One name for a survey, used by the dropdown and by the table alike.
   * Two screens naming the same survey two different ways is how somebody
   * comes to believe they are looking at two surveys.
   */
  const surveyName = useMemo(() => surveyLabels(surveys.data ?? []), [surveys.data])

  /*
   * Starts on every survey, which is what somebody coming to answer a question
   * actually wants: they know the job title, not which file it is in.
   *
   * It used to start on the newest survey, because a table drawn across
   * several of them had no single set of columns. The table shows the same
   * fields for every survey now, so there is nothing left to narrow around.
   */

  // Filters belong to the columns on screen, and choosing a survey changes
  // which columns those are.
  useEffect(() => { setNarrow({}) }, [survey])

  /*
   * A column's values, fetched when its filter is opened and kept.
   *
   * One column at a time. Fifty columns with a thousand values apiece is
   * several megabytes, and sending all of it to draw a table nobody has
   * filtered yet would be the whole survey over again.
   */
  const held = useRef(new Map<string, Promise<ColumnValues>>())
  useEffect(() => { held.current.clear() }, [survey])
  const loadValues = useCallback((surveyId: string, key: string) => {
    const at = `${surveyId}:${key}`
    const already = held.current.get(at)
    if (already) return already
    const asking = api.getColumnValues(surveyId, key)
    held.current.set(at, asking)
    // A failure must not be remembered as an answer, or the filter would stay
    // empty until the screen is reloaded.
    void asking.catch(() => held.current.delete(at))
    return asking
  }, [])

  const ask = useCallback<Ask>(
    ({ cursor, from }, want, signal) =>
      api.listMarketData({
        q,
        survey: survey || undefined,
        clauses: Object.values(narrow),
        ...(cursor ? { cursor } : {}),
        ...(from ? { from } : {}),
        limit: want,
        signal,
      }),
    [q, survey, narrow],
  )

  /**
   * Whether a page can be asked for directly rather than walked to.
   *
   * Inside one survey with nothing narrowing it, a row's place in the sort key
   * is its line, so page two thousand five hundred is the rows from line
   * 99,961 and the table can start there. A search or a filter breaks that,
   * because the rows on a page are then no longer the rows at that position.
   */
  const seekable = Boolean(survey) && !q.trim() && Object.keys(narrow).length === 0

  /*
   * One search at a time. Every load, page change and "keep searching" takes
   * a turn, taking one stops the one before, and only the latest may change
   * what is on screen.
   */
  const turns = useRef(latestOnly())

  /** Puts a finished page on screen. */
  const show = useCallback((got: Filled, at: number) => {
    setPage(got.page)
    setRows(got.page.items)
    setAt(at)
    setPaused(got.paused)
  }, [])

  /*
   * What a page being filled has found so far, so the rows already found are
   * on screen while the rest is read. Nothing yet leaves the screen as it was
   * rather than blanking it.
   */
  const soFar = useCallback((at: number) => (some: MarketPage) => {
    if (!some.items.length) return
    setPage(some)
    setRows(some.items)
    setAt(at)
    setPaused(false)
  }, [])

  /**
   * Goes to a page.
   *
   * Where the table can be started at a line, any page is one request. Where
   * it cannot, a cursor says only "carry on from here", so a page nobody has
   * been to is reached by reading the pages in front of it, each one filled
   * the way the page on screen is, so a page starts where the one before it
   * ended.
   *
   * **That walk is only ever a few pages long**, because a page number is not
   * offered past what walking can reach. A hundred thousand row survey once
   * offered page 2,500 on a table that had to read 2,499 pages to get there;
   * it was still going after eighteen minutes, with the buttons dead.
   *
   * Asking for a page past the end lands on the last page there is rather
   * than on an empty table.
   */
  const goTo = useCallback(
    async (want: number) => {
      const turn = turns.current.begin()
      setSearching(true)
      setFailed(null)
      const marks = [...trail]
      await lastWins(turn, async () => {
        if (seekable) {
          const got = await readPage(ask, { cursor: null, from: want * PAGE + 1 }, turn, soFar(want))
          return { got, landing: want }
        }
        let i = marks.length - 1
        let landed: Filled | null = null
        while (i < want) {
          const step = await readPage(ask, { cursor: marks[i] }, turn)
          // The last page, or one that could not be filled: there is nothing
          // to walk on to, so this is where it lands.
          if (!nextPageOpen(step.page)) { landed = step; break }
          marks[i + 1] = step.page.cursor
          i++
        }
        const landing = Math.min(want, i)
        const got = landed ?? await readPage(ask, { cursor: marks[landing] }, turn, soFar(landing))
        if (nextPageOpen(got.page) && marks[landing + 1] === undefined) marks[landing + 1] = got.page.cursor
        return { got, landing }
      }, {
        done: ({ got, landing }) => {
          setTrail(marks)
          show(got, landing)
        },
        failed: (message) => {
          setFailed(message)
          setPage(null)
          setRows([])
        },
        settled: () => setSearching(false),
      })
    },
    [ask, trail, seekable, show, soFar],
  )

  /*
   * A new search is a new table, so the cursors collected against the old one
   * mean nothing and the first page is the only one that can be asked for.
   *
   * Handed the turn the keystroke took, so the search it replaced stopped the
   * moment the words changed rather than a quarter of a second later.
   */
  const load = useCallback(async (turn = turns.current.begin()) => {
    setSearching(true)
    setFailed(null)
    const first = soFar(0)
    await lastWins(turn, () => readPage(ask, { cursor: null }, turn, (some) => {
      if (some.items.length) setTrail([null])
      first(some)
    }), {
      done: (got) => {
        // A second page only once the first is full. One match is one page.
        setTrail(nextPageOpen(got.page) ? [null, got.page.cursor] : [null])
        show(got, 0)
      },
      failed: (message) => {
        setFailed(message)
        setPage(null)
        setRows([])
        setTrail([null])
        setAt(0)
        setPaused(false)
      },
      settled: () => setSearching(false),
    })
  }, [ask, show, soFar])

  /**
   * Carries on filling the page on screen after it stopped at its most reads.
   *
   * The rows already found stay, and what is found next joins them, so the
   * page is the same page it would have been had it never paused.
   */
  const keepReading = useCallback(async () => {
    if (!page?.cursor) return
    const turn = turns.current.begin()
    const from = page.cursor
    const have = rows
    const here = at
    setSearching(true)
    setFailed(null)
    await lastWins(turn, () => readPage(ask, { cursor: from }, turn, soFar(here), have), {
      done: (got) => {
        if (nextPageOpen(got.page)) {
          setTrail((was) => {
            const marks = was.slice(0, here + 1)
            marks[here + 1] = got.page.cursor
            return marks
          })
        }
        show(got, here)
      },
      failed: (message) => setFailed(message),
      settled: () => setSearching(false),
    })
  }, [ask, page, rows, at, show, soFar])

  /*
   * Debounced, so typing does not fire a query per keystroke against a table
   * that may hold a hundred thousand rows.
   *
   * The turn is taken at the keystroke, not when the wait is over, so the
   * search for the letters before it stops straight away and cannot land on
   * screen in the quarter second before the new one starts.
   */
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => {
    const turn = turns.current.begin()
    setSearching(true)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void load(turn), 250)
    return () => {
      window.clearTimeout(timer.current)
      turns.current.end()
    }
  }, [load])

  const chosen = surveys.data?.find((s) => s.id === survey)
  /** The columns actually narrowed, which is what the screen counts and shows. */
  const set = useMemo(() => Object.keys(narrow), [narrow])

  /** Sets or releases one column's filter. */
  const onNarrow = useCallback((id: string, clause: FilterClause | null) => {
    setNarrow((prev) => {
      const next = { ...prev }
      if (clause) next[id] = clause
      else delete next[id]
      return next
    })
  }, [])
  const narrowed = Boolean(q.trim()) || set.length > 0

  /*
   * How many pages there are, where that can be answered.
   *
   * Sitting on a survey with nothing narrowing it, the count is stored and the
   * last page number is real. The moment a search or a filter is on, it is not
   * known and must not be guessed: the pages offered are the ones reached so
   * far, plus one while there is more to read.
   */
  /*
   * A survey still being stored. Its count is only set once the import
   * finishes, so until then it reads as holding nothing, and the screen took
   * that at its word: "Nothing matched" with nothing typed, and one page of 40
   * while thousands were already stored (STE-962). Not counted is not empty.
   */
  const storing = chosen ? chosen.rowCount === 0 : (surveys.data ?? []).some((s) => s.rowCount === 0)
  // How far it has got, "12,000 of 30,000 rows stored", where that can be said (FR-M58).
  const figure = storedAcross(chosen ? [chosen] : surveys.data ?? [])
  const everything = narrowed || storing ? null : chosen ? chosen.rowCount : page?.held ?? null

  /**
   * How far a page number may be offered when it has to be walked to.
   *
   * Every page between here and there is a request, so this is the number of
   * requests a person is asked to wait through for one click. A hundred
   * thousand row survey offered page 2,500 and the walk was still going after
   * eighteen minutes.
   */
  const WALKABLE = 25

  /*
   * Where the count is known and a page can be asked for directly, every page
   * number is real and instant. Where it is known but each page has to be
   * walked to, numbers are offered only as far as the walk stays short. Where
   * it is not known at all, only the pages a cursor exists for are offered,
   * because a number that turns out to be nothing is indistinguishable from
   * records having gone missing.
   */
  const counted = everything === null ? null : Math.max(1, Math.ceil(everything / PAGE))
  const exact = counted !== null && (seekable || counted <= WALKABLE)
  const pages = exact
    ? counted
    : counted !== null
      ? Math.min(counted, Math.max(trail.length, at + 1))
      : Math.max(trail.length, at + 1)
  const total = exact ? everything : null

  const spansSurveys = useMemo(
    () => new Set(rows.map((r) => r.survey)).size > 1,
    [rows],
  )

  if (surveys.loading) return <LoadingBlock label="Loading market data" />
  if (surveys.error) return <ErrorState message={surveys.error} onRetry={surveys.reload} />

  const surveyCount = surveys.data?.length ?? 0

  /*
   * One heading for both states. The empty state used to be returned on its
   * own, so a company holding nothing lost the heading, the Surveys button and
   * the space under the top bar: the first screen every new customer and every
   * new environment sees (STE-951).
   */
  const heading = (
    <header className="title-row">
      <div>
        {/* The same words as the navigation item. A heading and a nav entry
            differing by a letter reads as two screens. */}
        <h1 className="page">Market Data</h1>
        <p className="subline">
          {surveyCount ? `${plural(surveyCount, 'survey')} held` : 'No surveys held'}
        </p>
      </div>
      <div className="actions">
        <Link className="btn" to="/market-data/surveys">Surveys</Link>
        <Link className="btn btn--primary" to="/market-data/add">Add a survey</Link>
      </div>
    </header>
  )

  if (!surveys.data?.length) {
    return (
      <section className="screen">
        {heading}
        <div className="state state--empty">
          <h2>No market data yet</h2>
          <p>
            Add a survey and its rows appear here, under the provider's own column
            headings. Every provider's file is a different shape and all of them
            load as they are.
          </p>
          <Link className="btn btn--primary" to="/market-data/add">Add a survey</Link>
        </div>
      </section>
    )
  }

  return (
    <section className="screen">
      {heading}

      <div className="search-row">
        <label className="sr-only" htmlFor="market-search">Search market data</label>
        <input
          id="market-search"
          type="search"
          value={q}
          placeholder="Any word, across every survey you hold…"
          onChange={(e) => setQ(e.target.value)}
        />

        <label className="field">
          <span>Survey</span>
          <select value={survey} onChange={(e) => setSurvey(e.target.value)}>
            <option value="">Every survey</option>
            {/*
              Named the way the table names them: provider and year, with the
              file only where two share both.

              Provider and year alone are not enough to tell two surveys
              apart, and a compensation team legitimately holds several from
              one provider in one year. Three entries all reading "Mercer
              2026" had somebody pick the wrong one, see four rows, and
              reasonably conclude the rest of their upload had been lost. But
              a file name on every entry, where nothing is ambiguous, is a
              name nobody reads to the end.
            */}
            {surveys.data.map((s) => {
              const label = surveyName(s.id)
              return (
                <option key={s.id} value={s.id}>
                  {label.title}{label.file ? ` · ${label.file}` : ''}
                  {' · '}{s.rowCount.toLocaleString()} {s.rowCount === 1 ? 'record' : 'records'}
                  {s.removing ? ' · being removed' : ''}
                </option>
              )
            })}
          </select>
        </label>

        {/*
          Filters live on the column headings, the way a spreadsheet's do. A
          row of twenty five dropdowns above the table pushed the records
          themselves off the screen, and none of them said which column of the
          table they belonged to.
        */}
        {set.length > 0 && (
          <div className="filter-set">
            <span className="hint">
              {plural(set.length, 'column')} filtered
            </span>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setNarrow({})}
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {failed ? (
        <ErrorState message={failed} onRetry={() => void load()} />
      ) : !page ? (
        /*
          A search over a large survey takes a moment to fill its first page.
          It must never flash "nothing found" on the way to finding something:
          that looks exactly like a record that has gone missing, and somebody
          will believe it.

          Only before the first answer arrives, though. Replacing the table
          every time a page came back empty took the column headings away with
          it, and the filters live on those headings now: typing something that
          matched nothing closed the panel being typed into and left no way
          back except clearing every filter on the screen.
        */
        <LoadingBlock label="Searching" />
      ) : !rows.length && !survey && (searching || page.cursor) ? (
        /*
          Nothing found yet is not nothing found. While a search for these
          words is running, or more of the table is left to read, saying
          "Nothing matched" is how the right rows came to appear and then be
          replaced by it.
        */
        searching ? (
          <LoadingBlock label="Searching" />
        ) : (
          <div className="state">
            <h2>Nothing found yet</h2>
            <p>A long way into your surveys without a match, and there is more to read.</p>
            <button type="button" className="btn" onClick={() => void keepReading()}>
              Keep searching
            </button>
          </div>
        )
      ) : !rows.length && !survey ? (
        <div className="state">
          {/* "Matches that" only when something was searched or filtered. */}
          <h2>{narrowed ? 'Nothing matched' : storing ? 'Still being stored' : 'No records yet'}</h2>
          <p>
            {!narrowed
              ? storing
                ? figure
                  ? `${figure}. Its rows appear here as they are written.`
                  : 'Your survey is still being stored. Its rows appear here as they are written, so look again in a few minutes.'
                : 'Nothing is held in the surveys yet.'
              : set.length > 0
                ? 'Nothing matches that search with these filters set.'
                : 'Nothing in the surveys you hold matches that.'}
          </p>
          {set.length > 0 && (
            <button
              type="button"
              className="btn"
              onClick={() => setNarrow({})}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <RecordTable
            rows={rows}
            survey={chosen}
            spansSurveys={spansSurveys}
            surveys={surveys.data}
            more={Boolean(page?.more)}
            searching={searching}
            narrowed={narrowed}
            storing={storing}
            narrow={narrow}
            onNarrow={onNarrow}
            loadValues={loadValues}
          />

          <nav className="pager" aria-label="Record pages">
            {/*
              It cannot say "34 of 1,840 matches". Search reads a page at a
              time, so how many rows match is not known until the last page has
              been read, and the API deliberately does not return a total.

              Which means the page numbers differ by case, and honestly so.
              Sitting on a whole survey, the count is stored and every page
              number is real. Once a search or a filter narrows it, only the
              pages reached so far are known to exist, and one more is offered
              while there is more to read.
            */}
            <p className="subline">{countLine(rows.length, at, total, page, chosen, storing, figure)}</p>
            {/* The rows found so far stay put while the rest of the page is read. */}
            {searching && rows.length > 0 && (
              <p className="hint" role="status">Still searching…</p>
            )}
            {paused && !searching && (
              <p className="hint">
                This page is not full yet and there is more to read.{' '}
                <button type="button" className="btn btn--sm" onClick={() => void keepReading()}>
                  Keep searching
                </button>
              </p>
            )}
            <PageNumbers
              at={at}
              pages={pages}
              open={!exact && nextPageOpen(page)}
              busy={searching}
              onGo={(n) => void goTo(n)}
            />
          </nav>
        </>
      )}
    </section>
  )
}

/**
 * What the count line says, which depends on what is actually knowable.
 *
 * Searching reads the table a page at a time, so while there is more to read
 * the number of matches is genuinely unknown and must not be implied. Once
 * there is no more, every match is in hand and can be stated.
 */
function countLine(
  shown: number,
  at: number,
  total: number | null,
  page: MarketPage | null,
  survey: MarketSurvey | undefined,
  storing = false,
  figure: string | null = null,
): string {
  // Never "0 held" while rows are being written: that reads as data lost.
  const held = storing
    ? figure ?? 'still being stored'
    : survey
      ? `${survey.rowCount.toLocaleString()} in this survey`
      : `${(page?.held ?? 0).toLocaleString()} held across every survey`
  const rec = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'record' : 'records'}`

  if (total !== null) {
    // Every page is known, so the rows this page covers can be named exactly.
    const from = at * PAGE + 1
    const to = at * PAGE + shown
    if (!shown) return `No records · ${held}`
    return `${from.toLocaleString()} to ${to.toLocaleString()} of ${held}`
  }

  if (!shown) return `Nothing on this page · ${held}`
  const on = `Page ${at + 1} · ${rec(shown)} on it`
  return page?.more ? `${on}, more to read · ${held}` : `${on} · ${held}`
}

/**
 * The table itself.
 *
 * When the rows come from more than one survey there is no single set of
 * columns to draw, so it falls back to naming the survey and showing what
 * matched. That is not an edge case, it is what a search across everything
 * does.
 */
interface Narrowing {
  narrow: Record<string, FilterClause>
  onNarrow: (id: string, clause: FilterClause | null) => void
  loadValues: (surveyId: string, key: string) => Promise<ColumnValues>
}

/**
 * The records table: the same fields for every survey.
 *
 * **One shape, whichever survey is in view.** It used to draw each
 * provider's own columns, so a table of Mercer rows and a table of Radford
 * rows had different headings in different orders, and a search spanning
 * both drew a merged set that matched neither file.
 *
 * The fields are the ones the rest of the product already reads, and which
 * column holds each one is resolved on the server by the same functions the
 * benchmarking index and the matcher use.
 *
 * **Nothing is hidden, only moved.** Every column the provider published is
 * still stored, still searched, still indexed for the agent, and still on
 * the record's own screen. Somebody who wants all twenty two opens a row.
 */
function RecordTable({
  rows,
  survey,
  spansSurveys,
  surveys,
  more,
  searching,
  narrowed,
  storing,
  ...narrowing
}: {
  rows: MarketRecord[]
  survey?: MarketSurvey
  spansSurveys: boolean
  surveys: MarketSurvey[]
  /** Whether the survey has more to read past this page. */
  more: boolean
  /** Whether a search for what is on screen is still running. */
  searching: boolean
  /** Whether anything was searched or filtered. Only then can nothing "match". */
  narrowed: boolean
  /** Whether a survey is still being stored, so empty means not yet. */
  storing: boolean
} & Narrowing) {
  const byId = useMemo(() => new Map(surveys.map((s) => [s.id, s])), [surveys])
  const named = useMemo(() => surveyLabels(surveys), [surveys])

  /*
   * Which surveys have rows here. One survey reaching this table is normal
   * even with several held: a search can match nothing outside Mercer.
   */
  const present = useMemo(() => {
    if (survey && !spansSurveys) return new Set([survey.id])
    return new Set(rows.map((r) => r.survey))
  }, [rows, survey, spansSurveys])

  const { shown, constants } = useMemo(
    () => foldFields(surveys, present), [surveys, present],
  )

  const inView = surveys.filter((s) => present.has(s.id))
  const manySurveys = present.size > 1

  /*
   * Which surveys a column filter offers values from.
   *
   * **Not the surveys on this page.** `present` is read off `rows`, which is
   * one page of a table that may run to thousands, and a filter built from it
   * lists only what that page happens to hold. Page one here is all Mercer, so
   * the Job code filter offered Mercer's 56 codes, said "all 56", and left out
   * every code in the other eight surveys. Ticking a value you cannot see is
   * not possible, so the filter said the data was not there when it was.
   *
   * The Survey selector is what bounds a filter. One survey chosen means that
   * survey. Every survey means every survey that carries the column, whether
   * or not a row from it reached this page.
   *
   * `present` is left alone, because it answers a different question: which
   * columns are worth drawing. A search that matches only Mercer should not
   * draw eight surveys' empty columns beside it.
   */
  const filterAcross = useMemo(
    () => (survey && !spansSurveys ? surveys.filter((s) => s.id === survey.id) : surveys),
    [surveys, survey, spansSurveys],
  )

  /*
   * What every row says, said once. The survey where there is one, then the
   * measure the pay columns are from, then the unit and the currency, then
   * any field that holds a single value across every survey on screen.
   */
  const said: { label: string; value: string }[] = []
  if (!manySurveys && inView.length === 1) {
    said.push({ label: 'Survey', value: named(inView[0]!.id).title })
  }
  const first = inView[0]?.shown
  /*
   * Which measure each group of figures came from.
   *
   * Three sets of percentiles sit side by side and they are not the same
   * thing: base pay, the bonus-inclusive total, and equity. Naming them
   * here keeps the column headings short and stops a reader taking the
   * widest figure for a salary.
   */
  /*
   * "from", because the value is the provider's own name for the measure and
   * it usually starts with the same word as the label. Labelled "Base" it
   * read "Base Base salary, current fiscal year", which looks like a fault
   * rather than a sentence.
   */
  const measureNames = [
    ['Base pay from', 'measure'],
    ['Total cash from', 'cashMeasure'],
    ['Equity from', 'equityMeasure'],
  ] as const
  for (const [label, key] of measureNames) {
    const value = first?.[key]
    if (value && inView.every((s) => s.shown?.[key] === value)) {
      said.push({ label, value: String(value) })
    }
  }
  const sameUnit = inView.every((s) => s.shown?.unit === first?.unit)
  if (first?.unit && sameUnit) said.push({ label: 'Reported', value: first.unit })
  for (const c of constants) said.push({ label: FIELD_LABEL[c.field], value: c.value })

  return (
    <>
      <SaidOnce said={said} />
      <div className="table-wrap">
        <table className="table table--figures">
          <thead>
            <tr>
              {/* Which survey a row came from is the one thing the fields
                  cannot say, and it is what tells two otherwise identical
                  rows apart. It takes the frozen first column for that
                  reason, and only where there is more than one to tell
                  apart. */}
              {manySurveys && <th className="sticky-col cell--line">Survey</th>}
              {shown.map((field, i) => (
                <th key={field} className={cellClass(manySurveys ? i + 1 : i, FIELD_KIND[field])}>
                  {FIELD_LABEL[field]}
                  {/*
                    Filtered through every survey's own key for that field,
                    so a row passes when its own file's column matches.

                    No filter at all where no survey held carries the
                    field: its column is blank on every row, and a funnel
                    that opens on an empty list reads as a filter that is
                    broken rather than as a column nobody filled.
                  */}
                  {sourcesFor(filterAcross, field).length > 0 && (
                    <ColumnFilter
                      id={field}
                      heading={FIELD_LABEL[field]}
                      sources={sourcesFor(filterAcross, field)}
                      {...narrowing}
                    />
                  )}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              /*
                The headings stay, because they carry the filters. A table
                that disappears when nothing matches takes away the control
                that narrowed it, which is the one thing a person needs at
                that moment.
              */
              <tr>
                <td className="table__empty" colSpan={shown.length + (manySurveys ? 2 : 1)}>
                  {/* Only where there is genuinely nothing left to read.
                      Saying it while the survey has more is how a record
                      that is there gets reported as missing. */}
                  {searching
                    ? 'Still searching…'
                    : more
                    ? 'No matches yet. There is more of this survey to read.'
                    : !narrowed && storing
                      ? 'Still being stored. Its rows appear here as they are written.'
                      : survey
                      ? 'Nothing in this survey matches that.'
                      : 'Nothing in the surveys you hold matches that.'}
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const from = byId.get(row.survey)
              return (
                <tr key={row.id}>
                  {/*
                    The provider and the year, and not the file.

                    A name like "Mercer Market Data Results
                    Para-Professional_20260506094414.xlsx" wrapped to three
                    lines in a frozen column, which then covered the job code
                    and the job title sitting behind it. Telling two surveys
                    of one provider and year apart is what the picker above
                    the table is for, and it still names the file there.
                  */}
                  {manySurveys && (
                    <td className="sticky-col cell--line">{named(row.survey).title}</td>
                  )}
                  {shown.map((field, i) => {
                    const key = keyOf(from, field)
                    return (
                      <td
                        key={field}
                        className={cellClass(manySurveys ? i + 1 : i, FIELD_KIND[field])}
                      >
                        {/*
                          Exactly as it was stored. No separators added, no
                          rounding, no currency symbol invented, no unit
                          assumed. If the provider wrote 85.5 the cell reads
                          85.5, because we do not know whether that is
                          thousands and must not imply we do.

                          Blank where this file does not carry the field. The
                          set of columns is the same for every survey, and a
                          blank is the truth about that file rather than a
                          gap in the screen.
                        */}
                        {key ? row.values[key] ?? '' : ''}
                      </td>
                    )
                  })}
                  <td>
                    <Link className="btn btn--sm" to={`/market-data/${row.id}`}>Open</Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * What every row on this table says, said once.
 *
 * Nothing at all where nothing is constant, which is the normal case for a
 * survey spanning several countries. An empty strip above a table is its own
 * kind of noise.
 */
function SaidOnce({ said }: { said: { label: string; value: string }[] }) {
  if (said.length === 0) return null
  return (
    <p className="said-once">
      <span className="said-once__lead">Every row:</span>
      {said.map((s) => (
        <span key={s.label} className="said-once__item">
          <span className="said-once__of">{s.label}</span> {s.value}
        </span>
      ))}
    </p>
  )
}

/**
 * Which survey columns a field is filtered through.
 *
 * Given the surveys the Survey selector covers, not the ones whose rows
 * reached the open page. A value held only by a survey further down the table
 * is still a value the column can hold, and a filter that leaves it out cannot
 * be used to find it.
 */
const sourcesFor = (across: MarketSurvey[], field: ShownField) =>
  across
    .map((s) => ({ survey: s.id, key: keyOf(s, field) }))
    .filter((x): x is { survey: string; key: string } => Boolean(x.key))

/**
 * Which class a cell takes.
 *
 * The first column stays put while the rest scrolls sideways: without it,
 * somebody reading a percentile cannot tell which row they are on, and
 * reading the wrong row's number is the worst thing this screen can cause.
 *
 * **Text stays on one line.** Survey job titles are long, and wrapped they
 * make every row two lines tall, so a reader scanning for one job reads a
 * ragged column instead of a list. The table already scrolls sideways.
 */
const cellClass = (i: number, kind: SurveyColumn['kind']) => {
  const line = kind === 'text' ? 'cell--line' : 'num'
  // The frozen column is usually the job code or title, and it wrapped like
  // any other cell: "Principal Perception Engineer" came apart over two
  // lines and stood that whole row taller than the rest of the table.
  return i === 0 ? `sticky-col ${line}` : line
}

/**
 * The filter that lives on a column heading, the way a spreadsheet's does.
 *
 * Ticked values are the ones the column may hold, so ticking three levels
 * shows all three rather than making somebody choose between them. Nothing
 * ticked means the column is not filtered at all, which is why clearing it
 * and ticking everything come to the same thing.
 *
 * A column with too many distinct values to list takes typing instead. That
 * is not a lesser filter, it is the only one that can work: a pay column in a
 * survey of a hundred thousand rows has a hundred thousand distinct figures,
 * and a list of them is the column again.
 *
 * The values are fetched when the panel opens rather than sent with the table.
 * Fifty columns with a thousand values apiece is several megabytes, and a
 * table nobody has filtered yet needs none of it.
 *
 * The panel is drawn in a layer fixed to the window rather than inside the
 * heading. The table scrolls sideways inside its own box, and a panel drawn
 * inside that box is cut off at its edge, which is exactly where the columns a
 * person most wants to filter tend to be.
 */
function ColumnFilter({
  id,
  heading,
  sources,
  narrow,
  onNarrow,
  loadValues,
}: {
  /** Stable name for this column across surveys. Its heading, lower cased. */
  id: string
  heading: string
  /** The surveys this column appears in, and its key in each. */
  sources: { survey: string; key: string }[]
  narrow: Record<string, FilterClause>
  onNarrow: (id: string, clause: FilterClause | null) => void
  loadValues: (surveyId: string, key: string) => Promise<ColumnValues>
}) {
  const [open, setOpen] = useState(false)
  const [spot, setSpot] = useState<{ top: number; left: number } | null>(null)
  const [find, setFind] = useState('')
  const [values, setValues] = useState<ColumnValues | null>(null)
  const [loading, setLoading] = useState(false)
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  const clause = narrow[id]
  const on = Boolean(clause)
  const keys = useMemo(() => sources.map((s) => s.key), [sources])
  const text = clause?.contains ?? ''
  const chosen = useMemo(() => clause?.any ?? [], [clause])

  /*
   * The values, once, when the panel first opens.
   *
   * Where a column comes from several surveys they are put together, because
   * on that table the column is all of them at once and a value held by only
   * the second survey is still a value in the column.
   */
  useEffect(() => {
    if (!open || values || loading) return
    setLoading(true)
    Promise.all(sources.map((s) => loadValues(s.survey, s.key)))
      .then((parts) => {
        const tooMany = parts.some((p) => p.tooMany)
        const set = new Set<string>()
        for (const part of parts) for (const v of part.values) set.add(v)
        setValues({
          key: keys[0],
          heading,
          tooMany,
          partial: parts.some((p) => p.partial),
          values: tooMany
            ? []
            : [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
        })
      })
      .catch(() => setValues({ key: keys[0], heading, values: [], tooMany: true }))
      .finally(() => setLoading(false))
    // Fetched on opening and kept. Re-reading while it is open would replace
    // the list under the ticks being made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const listed = Boolean(values && !values.tooMany && values.values.length > 0)

  /*
   * What is ticked while the panel is open.
   *
   * Held here rather than read back from the filter, because there is a state
   * the filter cannot hold: nothing ticked. Narrowing a column the way a
   * spreadsheet does it means clearing the ticks and then putting back the two
   * or three wanted, and for that moment nothing is ticked. An empty filter
   * means the column is not narrowed at all, so sending it would show every
   * row at exactly the point the person is asking for fewer.
   *
   * So nothing ticked changes nothing yet, and the panel says as much.
   */
  const [draft, setDraft] = useState<string[]>([])
  useEffect(() => {
    if (open && listed) setDraft(chosen.length ? chosen : values!.values)
    // Only when it opens, or when the list first arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listed])

  const ticked = useMemo(() => new Set(draft), [draft])
  const all = listed && draft.length === values!.values.length
  const showing = useMemo(() => {
    const held = values?.values ?? []
    const term = find.trim().toLowerCase()
    return term ? held.filter((v) => v.toLowerCase().includes(term)) : held
  }, [values, find])

  /*
   * Where the panel sits, kept against the heading it belongs to.
   *
   * It follows a scroll rather than closing on one. Closing looked tidier and
   * was wrong: opening the panel focuses its search box, and focusing anything
   * inside a table that scrolls sideways makes that table scroll a little, so
   * the panel shut itself the instant it opened. It only showed on a wide
   * survey, where the table has somewhere to scroll to.
   */
  useEffect(() => {
    if (!open) return
    const place = () => {
      const box = button.current?.getBoundingClientRect()
      if (!box) return
      const width = 260
      // Held inside the window, so a filter on the last column does not open
      // half off the right edge.
      const left = Math.max(8, Math.min(box.left, window.innerWidth - width - 8))
      setSpot({ top: box.bottom + 4, left })
    }
    place()
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const away = (e: MouseEvent) => {
      const at = e.target as Node
      if (panel.current?.contains(at) || button.current?.contains(at)) return
      setOpen(false)
    }
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    window.addEventListener('keydown', key)
    document.addEventListener('mousedown', away)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
      window.removeEventListener('keydown', key)
      document.removeEventListener('mousedown', away)
    }
  }, [open])

  /**
   * Applies a set of ticks, where everything ticked means no filter at all.
   *
   * Sent as whichever side is shorter. Unticking one value out of four hundred
   * leaves three hundred and ninety nine ticked, and asking for all of them by
   * name is far past the size of question the database will accept: the screen
   * answered "that did not load" and gave no clue which tick had caused it.
   * Naming the one that was unticked asks the same thing in a few characters.
   */
  function apply(list: string[]) {
    setDraft(list)
    if (!list.length) return
    onNarrow(id, sideOf(list, values?.values ?? [], keys))
  }

  function toggle(value: string) {
    const next = new Set(ticked)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    apply([...next])
  }

  function type(value: string) {
    onNarrow(id, value.trim() ? { keys, contains: value } : null)
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        className={on ? 'colfilter__open is-on' : 'colfilter__open'}
        aria-label={on ? `Filtering ${heading}` : `Filter ${heading}`}
        aria-expanded={open}
        onClick={() => { setFind(''); setOpen((was) => !was) }}
      >
        {/* A funnel, drawn rather than loaded, so it needs no icon set. */}
        <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
          <path d="M1 2h14l-5.5 6.5V14L6.5 12V8.5z" fill="currentColor" />
        </svg>
      </button>

      {open && spot && (
        <div className="colfilter" ref={panel} style={{ top: spot.top, left: spot.left }}>
          <div className="colfilter__head">{heading}</div>

          {loading || !values ? (
            <p className="colfilter__none">Reading this column…</p>
          ) : listed ? (
            <>
              <input
                className="colfilter__find"
                type="search"
                value={find}
                placeholder="Find a value…"
                autoFocus
                onChange={(e) => setFind(e.target.value)}
              />
              <label className="colfilter__row">
                {/* Ticks everything, which is the same as no filter, or clears
                    the ticks so a few can be put back. */}
                <input
                  type="checkbox"
                  checked={all}
                  onChange={() => (all ? setDraft([]) : apply(values.values))}
                />
                <span>(Select all)</span>
              </label>
              <div className="colfilter__list">
                {showing.length === 0 ? (
                  <p className="colfilter__none">Nothing in this column matches that.</p>
                ) : (
                  showing.map((v) => (
                    <label className="colfilter__row" key={v}>
                      <input type="checkbox" checked={ticked.has(v)} onChange={() => toggle(v)} />
                      <span>{v}</span>
                    </label>
                  ))
                )}
              </div>
              {values.partial && (
                <p className="colfilter__none">
                  This survey was too large to read in one go, so this is the start of
                  the list. Anything missing can still be typed into the search box above
                  the table.
                </p>
              )}
            </>
          ) : (
            /*
              Too many distinct values to list, so she types.
             
              Matched on part of the value rather than the whole of it, and
              ignoring capitals, because nobody types a job title out in full
              to find it.
            */
            <>
              <input
                className="colfilter__find"
                type="search"
                value={text}
                placeholder="Contains…"
                autoFocus
                onChange={(e) => type(e.target.value)}
              />
              <p className="colfilter__none">
                Too many different values in this column to list. Shows rows where it
                contains what you type; part of the value is enough and capitals do not
                matter.
              </p>
            </>
          )}

          <div className="colfilter__foot">
            <span className="hint">
              {!values
                ? ''
                : listed
                  ? draft.length === 0
                    ? 'Tick a value'
                    : all
                      ? `all ${values.values.length}`
                      : `${draft.length} of ${values.values.length}`
                  : text.trim()
                    ? 'Narrowed'
                    : 'Not narrowed'}
            </span>
            <div className="actions">
              {on && (
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => {
                    if (listed) setDraft(values!.values)
                    onNarrow(id, null)
                  }}
                >
                  Clear
                </button>
              )}
              <button type="button" className="btn btn--sm" onClick={() => setOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
